// components/dashboard/CompanyPickerModal.jsx
import { useState, useEffect, useRef } from "react"
import { Search, X, Check, Briefcase, Component } from "lucide-react"

const FF = "'Euclid Circular A', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
const COMPANIES_ENDPOINT = "https://jobscanner-e1dd4.ue.r.appspot.com/network/companies"
const BRAND = "#18181b"

export default function CompanyPickerModal({ selected, onToggle, onClear, onClose, onDone, C }) {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [search, setSearch] = useState("")
  const searchRef = useRef(null)

  const fetchCompanies = () => {
    setFetchError(false)
    setLoading(true)
    fetch(COMPANIES_ENDPOINT)
      .then(res => { if (!res.ok) throw new Error("Failed"); return res.json() })
      .then(data => setCompanies(data.companies || []))
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchCompanies() }, [])

  useEffect(() => {
    if (!loading && searchRef.current) searchRef.current.focus()
  }, [loading])

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", h)
    return () => document.removeEventListener("keydown", h)
  }, [onClose])

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  )

  const isActive = selected.length > 0
  const selectedSet = new Set(selected)

  // Partition: selected pills first, then rest alphabetically
  const selectedCompanyObjs = companies.filter(c => selectedSet.has(c.name))
  const unselectedFiltered = filtered.filter(c => !selectedSet.has(c.name))

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: C.isDark ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.4)",
          zIndex: 300,
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          animation: "backdropFadeIn 0.15s ease",
        }}
      />

      {/* Modal */}
      <div style={{
        position: "fixed",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "45vw",
        height: "60vh",
        background: C.popupBg,
        borderRadius: 20,
        boxShadow: C.isDark
          ? "0 32px 80px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.07)"
          : "0 32px 80px rgba(0,0,0,0.20), 0 0 0 1px rgba(0,0,0,0.06)",
        zIndex: 301,
        display: "flex",
        flexDirection: "column",
        animation: "modalSlideIn 0.22s cubic-bezier(0.22,1,0.36,1)",
        overflow: "hidden",
      }}>

        {/* ── Header ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 18px 12px",
          borderBottom: `1px solid ${C.popupDivider}`,
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Refined icon: stacked squares suggesting a company/org */}
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: C.isDark ? "#1a2e25" : "#eef4f1",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Component size={16} color={C.isDark ? "#18181b" : BRAND} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: C.text, fontFamily: FF, lineHeight: 1.25, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Filter by Company
              </p>
              {!loading && !fetchError && (
                <p style={{ margin: 0, fontSize: 11, color: C.textTertiary, fontFamily: FF, marginTop: 1 }}>
                  {companies.length.toLocaleString()} companies · {selected.length} selected
                </p>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isActive && (
              <button
                onClick={onClear}
                style={{
                  fontSize: 11.5, fontWeight: 550, color: "#e05050",
                  background: C.isDark ? "rgba(220,38,38,0.08)" : "#fef5f5",
                  border: `1px solid ${C.isDark ? "rgba(220,38,38,0.18)" : "#fdd"}`,
                  borderRadius: 7, padding: "4px 10px",
                  cursor: "pointer", fontFamily: FF,
                  display: "flex", alignItems: "center", gap: 4,
                  transition: "all 0.12s",
                }}
              >
                <X size={10} /> Clear {selected.length}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                width: 30, height: 30, borderRadius: 8,
                border: `1px solid ${C.borderColor}`,
                background: C.bgMuted, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: C.textDim, transition: "all 0.12s", flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = C.bgCard; e.currentTarget.style.color = C.text }}
              onMouseLeave={e => { e.currentTarget.style.background = C.bgMuted; e.currentTarget.style.color = C.textDim }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* ── Search ── */}
        <div style={{ padding: "12px 16px 0", flexShrink: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: C.inputBg,
            border: `1px solid ${C.inputBorder}`,
            borderRadius: 11, padding: "9px 13px",
            transition: "border-color 0.15s, box-shadow 0.15s",
          }}
            onFocus={() => { }}
          >
            <Search size={13} color={C.textTertiary} style={{ flexShrink: 0 }} />
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search companies…"
              style={{
                flex: 1, border: "none", outline: "none",
                background: "transparent",
                fontSize: 13, fontFamily: FF, color: C.text,
              }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                style={{ background: "none", border: "none", cursor: "pointer", color: C.textDim, display: "flex", padding: 0 }}
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* ── Selected pills strip ── */}
        {selectedCompanyObjs.length > 0 && (
          <div style={{
            padding: "10px 16px 0",
            flexShrink: 0,
          }}>
            <div style={{
              display: "flex", alignItems: "center", flexWrap: "wrap", gap: 5,
            }}>
              {selectedCompanyObjs.map(company => (
                <span
                  key={company.name}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "5px 10px 5px 12px",
                    background: C.isDark ? "#1a2e25" : "#e8f4ee",
                    border: `1px solid ${C.isDark ? "#18181b44" : "#18181b33"}`,
                    borderRadius: 6,
                    fontSize: 12, fontWeight: 550,
                    color: C.isDark ? "#18181b" : BRAND,
                    fontFamily: FF,
                    whiteSpace: "nowrap",
                    animation: "fadeUp 0.15s ease",
                  }}
                >
                  {company.name}
                  <span style={{
                    fontSize: 10, fontWeight: 500,
                    color: C.isDark ? "#18181b99" : "#18181b88",
                    marginRight: 2,
                  }}>{company.count.toLocaleString()}</span>
                  <button
                    onClick={() => onToggle(company.name)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: "none", border: "none", cursor: "pointer", padding: 0,
                      color: C.isDark ? "#18181b99" : "#18181b88",
                      lineHeight: 1,
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = "#e05050"}
                    onMouseLeave={e => e.currentTarget.style.color = C.isDark ? "#18181b99" : "#18181b88"}
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Divider ── */}
        <div style={{ height: 1, background: C.popupDivider, margin: "12px 0 0", flexShrink: 0 }} />

        {/* ── Pills grid ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px 6px" }}>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 160, gap: 12 }}>
              <div style={{ width: 24, height: 24, border: `2px solid ${C.borderColor}`, borderTopColor: C.isDark ? "#18181b" : BRAND, borderRadius: "50%", animation: "spin 0.75s linear infinite" }} />
              <span style={{ fontSize: 12, color: C.textDim, fontFamily: FF }}>Fetching companies…</span>
            </div>
          ) : fetchError ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 160, gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.isDark ? "#2a1a1a" : "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={16} color="#dc2626" />
              </div>
              <p style={{ fontSize: 13, color: C.textDim, fontFamily: FF, margin: 0 }}>Failed to load companies</p>
              <button
                onClick={fetchCompanies}
                style={{ fontSize: 12, fontWeight: 600, color: C.isDark ? "#18181b" : BRAND, background: "none", border: "none", cursor: "pointer", fontFamily: FF }}
              >
                Retry
              </button>
            </div>
          ) : filtered.length === 0 && !selectedCompanyObjs.length ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120, color: C.textDim, fontSize: 13, fontFamily: FF }}>
              No companies match "{search}"
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {unselectedFiltered.map(company => {
                const isChecked = selectedSet.has(company.name)
                return (
                  <button
                    key={company.name}
                    onClick={() => onToggle(company.name)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "8px 16px",
                      background: isChecked
                        ? (C.isDark ? "#1a2e25" : "#e8f4ee")
                        : (C.isDark ? "#242424" : "#f3f4f2"),
                      border: `1px solid ${isChecked
                        ? (C.isDark ? "#18181b44" : "#18181b33")
                        : (C.isDark ? "#333" : "#e2e5e3")}`,
                      borderRadius: 10,
                      cursor: "pointer",
                      fontFamily: FF,
                      transition: "background 0.12s, border-color 0.12s, transform 0.1s",
                      flexShrink: 0,
                    }}
                    onMouseEnter={e => {
                      if (!isChecked) {
                        e.currentTarget.style.background = C.isDark ? "#2a2a2a" : "#eaece9"
                        e.currentTarget.style.borderColor = C.isDark ? "#444" : "#c8ccc9"
                      }
                      e.currentTarget.style.transform = "translateY(-1px)"
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = isChecked
                        ? (C.isDark ? "#1a2e25" : "#e8f4ee")
                        : (C.isDark ? "#242424" : "#f3f4f2")
                      e.currentTarget.style.borderColor = isChecked
                        ? (C.isDark ? "#18181b44" : "#18181b33")
                        : (C.isDark ? "#333" : "#e2e5e3")
                      e.currentTarget.style.transform = "translateY(0)"
                    }}
                  >
                    <span style={{
                      fontSize: 12.5, fontWeight: isChecked ? 600 : 450,
                      color: isChecked
                        ? (C.isDark ? "#18181b" : BRAND)
                        : C.textMuted,
                      fontFamily: FF,
                      lineHeight: 1,
                    }}>
                      {company.name}
                    </span>
                    <span style={{
                      fontSize: 10.5, fontWeight: 500,
                      color: isChecked
                        ? (C.isDark ? "#18181b88" : "#18181b88")
                        : C.textTertiary,
                      fontFamily: FF,
                      lineHeight: 1,
                    }}>
                      {company.count.toLocaleString()}
                    </span>
                  </button>
                )
              })}

              {unselectedFiltered.length === 0 && search && selectedCompanyObjs.length > 0 && (
                <p style={{ fontSize: 12, color: C.textDim, fontFamily: FF, padding: "8px 0" }}>
                  No other companies match "{search}"
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: "10px 16px 14px",
          borderTop: `1px solid ${C.popupDivider}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
          background: C.isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.01)",
        }}>
          <span style={{ fontSize: 11.5, color: C.textTertiary, fontFamily: FF }}>
            {selected.length > 0
              ? `${selected.length} compan${selected.length === 1 ? "y" : "ies"} selected`
              : "No filter applied"}
          </span>
          <button
            onClick={onDone}
            style={{
              height: 34, padding: "0 22px",
              background: BRAND, border: "none", borderRadius: 9,
              fontSize: 13, fontWeight: 600, color: "#fff",
              fontFamily: FF, cursor: "pointer",
              transition: "opacity 0.15s, transform 0.1s",
              letterSpacing: "0.01em",
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.transform = "translateY(-1px)" }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)" }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  )
}